import React, { FC, SyntheticEvent, useEffect, useMemo, useState, useCallback } from 'react';
import { RadioOption, Select } from '@td-client-frontend/common';
import { ExclamationTriangle, Lock } from '@td-client-frontend/icons';
import clsx from 'clsx';
import * as _ from 'lodash';
import { TierRulesTooltip, TierTooltip } from './tooltips';
import { useAuth } from '../../../../../context/AuthContext';
import {
  ChannelOption,
  MarketingPreferencesRadioButtonsGroup,
  RegAndRetentionData,
  RegistrationAndRetentionFormType,
  TabType,
  MarketingProfileById
} from '../../../../../@types/Marketing';
import { dropdownOptions, radioButtonsGroup, sections } from './constants';
import { PermMode } from '../../../../WithPermission';
import { Permissions } from '../../../../../@types/Permissions';

interface Props {
  profileData: MarketingProfileById;
  isReadOnly?: boolean;
  onChange: (data: RegAndRetentionData) => void;
  onValid?: (data: RegAndRetentionData) => void;
  onInvalid?: () => void;
}

export const RegistrationAndRetention: FC<Props> = ({
  profileData,
  onChange,
  onValid,
  onInvalid,
  isReadOnly = false
}) => {
  const {
    settings: { registration, retention }
  } = profileData;
  const regAndRetData = { registration, retention };

  const { checkPerm } = useAuth();

  const [errorMessage, setErrorMessage] = useState<Record<RegistrationAndRetentionFormType, string>>({
    [RegistrationAndRetentionFormType.REGISTRATION]: '',
    [RegistrationAndRetentionFormType.RETENTION]: ''
  });

  const [canRead] = useState<Record<RegistrationAndRetentionFormType, boolean>>({
    [RegistrationAndRetentionFormType.REGISTRATION]: checkPerm([Permissions.MarketingProfileRegistrationRead]),
    [RegistrationAndRetentionFormType.RETENTION]: checkPerm([Permissions.MarketingProfileRetentionRead])
  });

  const isDisabled: Record<string, boolean> = useMemo(() => {
    const hasAllCreate: boolean = checkPerm(
      [
        Permissions.MarketingProfileRegistrationCreate,
        Permissions.MarketingProfilePreferencesCreate,
        Permissions.MarketingProfileRetentionCreate
      ],
      PermMode.ALL
    );
    return {
      [RegistrationAndRetentionFormType.REGISTRATION]:
        isReadOnly ||
        (!hasAllCreate
          ? profileData.default
            ? !(
                checkPerm([Permissions.MarketingProfileBaseWrite]) &&
                checkPerm([Permissions.MarketingProfileRegistrationWrite])
              )
            : !checkPerm([Permissions.MarketingProfileRegistrationWrite])
          : false),
      [RegistrationAndRetentionFormType.RETENTION]:
        isReadOnly ||
        (!hasAllCreate
          ? profileData.default
            ? !(
                checkPerm([Permissions.MarketingProfileBaseWrite]) &&
                checkPerm([Permissions.MarketingProfileRetentionWrite])
              )
            : !checkPerm([Permissions.MarketingProfileRetentionWrite])
          : false)
    };
  }, [checkPerm, isReadOnly, profileData.default]);

  const setErrorForType = useCallback(
    (type: RegistrationAndRetentionFormType, error: string) => setErrorMessage?.({ ...errorMessage, [type]: error }),
    [errorMessage, setErrorMessage]
  );

  const checkForValid = useCallback(
    (data: Partial<RegAndRetentionData>): boolean => {
      for (const type of Object.values(RegistrationAndRetentionFormType).filter((type) => canRead[type])) {
        for (const field of ['tier', ...radioButtonsGroup.map(({ id }) => id)]) {
          // @ts-ignore
          if (!data[type] || data[type][field] === undefined) {
            return false;
          }
        }

        if (errorMessage[type]) {
          return false;
        }
      }

      return true;
    },
    [canRead, errorMessage]
  );

  const onFormChange = useCallback(
    (name: string) => (a: string | SyntheticEvent) => {
      const newData: Record<string, any> = { ...regAndRetData };

      const value: string = typeof a === 'string' ? a : (a.target as HTMLInputElement).value;
      const [type, field] = name.split('.');

      if (!newData[type]) {
        newData[type] = {};
      }

      // If modifying the tier, set the channels to a default value
      if (field === 'tier') {
        const channelValue: string = ['3', '4'].includes(value.toString()) ? ChannelOption.NO : ChannelOption.YES;
        radioButtonsGroup.forEach(({ id }) => (newData[type][id] = channelValue));
        // @ts-ignore
        setErrorForType(RegistrationAndRetentionFormType[type.toUpperCase()], '');
      }

      // Set new value
      newData[type][field] = value;

      // If modifying email or directMail and tier is 1, 2 or 5 then ensure that one of email or directMail is 'yes'
      if (
        ['email', 'directMail'].includes(field) &&
        _.get(newData, [type, 'tier'], false) &&
        ['1', '2', '5'].includes(_.get(newData, [type, 'tier'], '').toString())
      ) {
        const hasEmailOrDirectMail =
          _.get(newData, [type, 'email'], '') === ChannelOption.YES ||
          _.get(newData, [type, 'directMail'], '') === ChannelOption.YES;
        if (hasEmailOrDirectMail) {
          // @ts-ignore
          setErrorForType(RegistrationAndRetentionFormType[type.toUpperCase()], '');
        } else {
          setErrorForType(
            // @ts-ignore
            RegistrationAndRetentionFormType[type.toUpperCase()],
            'Minimum of Email or Direct Mail must be set to Yes for selected tier'
          );
        }
      }
      onChange(newData);
    },
    [onChange, regAndRetData, setErrorForType]
  );

  useEffect(() => {
    if (checkForValid(regAndRetData)) {
      if (onValid) {
        onValid(regAndRetData);
      }
    } else if (onInvalid) {
      onInvalid();
    }
  });

  return (
    <div className="flex flex-wrap" data-testid={TabType.RegRetention}>
      {sections
        .filter(({ type }) => canRead[type])
        .map((section, idx) => {
          const { type, label } = section;
          return (
            <div key={`${type}-section`} className="w-136 font-effra mb-8" data-testid={`${type}-section`}>
              <p className="text-xl pb-6 text-gray-45">{label}</p>

              <TierTooltip type={type} />

              <div className="max-w-md mb-6">
                <Select
                  name={`${type}.tier`}
                  options={dropdownOptions}
                  placeholder="Tier"
                  fieldLabelClasses="hidden"
                  onChange={onFormChange(`${type}.tier`)}
                  value={_.get(regAndRetData, [type, 'tier'])}
                  errorClassName="hidden"
                  isError={!!errorMessage[type]}
                  isDisabled={isDisabled[type]}
                  rightIcon={
                    isDisabled[type] && (
                      <div className="h-full flex items-center justify-center w-12 shadow-field">
                        <Lock />
                      </div>
                    )
                  }
                />
                {errorMessage[type] && (
                  <div className="flex space-x-2" data-testid={`${type}-item-error`}>
                    <ExclamationTriangle className="mt-1" data-testid={`${type}-item-error-icon`} />
                    <p className="font-effra text-gray-65 text-lg leading-5">{errorMessage[type]}</p>
                  </div>
                )}
              </div>

              <TierRulesTooltip type={type} />

              <div className="space-y-3">
                {radioButtonsGroup.map((radioButtonsGroup: MarketingPreferencesRadioButtonsGroup) => {
                  const { options, id, label } = radioButtonsGroup;

                  return (
                    <div
                      key={id}
                      className="flex items-center justify-start space-x-2 text-sm tracking-wide font-effra text-black-default"
                      data-testid={`${type}-channels`}
                    >
                      <div className="w-32">
                        <p className="font-effra text-lg text-gray-60">{label}</p>
                      </div>
                      {options
                        .filter(
                          ({ value }) =>
                            type !== RegistrationAndRetentionFormType.RETENTION ||
                            (type === RegistrationAndRetentionFormType.RETENTION &&
                              value !== ChannelOption.NOT_PROVIDED)
                        )
                        .map(({ value, label: optionLabel }) => {
                          const input = {
                            type: 'radio',
                            checked: _.get(regAndRetData, [type, id]) === value,
                            disabled:
                              // @ts-ignore
                              ([3, 4].includes(_.get(regAndRetData, [type, 'tier'])) && value === ChannelOption.YES) ||
                              isDisabled[type]
                          };
                          return (
                            <RadioOption
                              key={`${type}.${id}.${optionLabel}`}
                              name={`${type}.${id}`}
                              label={optionLabel}
                              value={value}
                              input={input}
                              onChange={onFormChange(`${type}.${id}`)}
                              labelClassName="group flex items-center justify-center space-x-3 sm:space-x-1 md:space-x-3 cursor-pointer font-effra text-lg text-gray-65 "
                              checkboxClassName={clsx(
                                'flex mr-3 h-5 w-5',
                                input.disabled ? 'bg-gray-10 ' : 'hover:border-blue-80',
                                input.checked && !isDisabled[type] && 'border-blue-80'
                              )}
                              activeCheckBoxClassName={isDisabled[type] ? 'bg-gray-20' : 'bg-blue-80'}
                            />
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
    </div>
  );
};
